import {
  OpenQuestionAnswerEntry,
  OpenQuestionAnswersForItems,
} from '../../../../reducers/student/tracking/openQuestionAnswersReducer';
import { createPreviouslySelectedOptionsForItems } from '../../../../reducers/student/tracking/questionWidgetTrackingReducer';
import { CommaSerializer } from '../serializer/Serializer';
import {
  CourseCompletionStatus,
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  QuizStatus,
  TrackingDataVersion,
} from '../trackingClient';
import { ScormClient, scormClient } from './scormTyped';
import { createScormInteractionsLocalStorage } from './interactionsLocalStorage';
import type { ScormInteractionsLocalStorage } from './interactionsLocalStorage';
import type { TrackingIdentifier } from '@ael/core-api';
import Immutable from 'immutable';
import { Map } from 'immutable';
import { getInteractionsFromLms2004, storeInteractionsToLms2004 } from './interactions2004';
import { diffOpenQuestionAnswers, storeInteractionsToLms12 } from './interactions12';

async function _delay(delayTimeMs: number) {
  return new Promise((r) => setTimeout(r, delayTimeMs));
}

export class ScormTrackingClient implements IPersistenceTrackingClient {
  private _scormClient: ScormClient;
  private _storage: ScormInteractionsLocalStorage;
  // ES2020-safe global replace. Once the build floor is raised to ES2021+ (see target/lib in
  // tsconfig.json and build.target in vite.config.ts), these can be simplified back to
  // jsonString.replaceAll('"', '$') / jsonString.replaceAll('$', '"').
  private _replaceQuoteWithDollarSign = (jsonString: string) => jsonString.split('"').join('$');
  private _replaceDollarSignWithQuote = (jsonString: string) => jsonString.split('$').join('"');
  private _cachedOpenQuestionAnswers: OpenQuestionAnswersForItems | null = null;

  constructor(storage: ScormInteractionsLocalStorage = createScormInteractionsLocalStorage()) {
    this._storage = storage;
    window.addEventListener('beforeunload', (e) => {
      if (this._scormClient.connection.isActive) {
        e.preventDefault();
        return void this.terminateConnection().then(() => (e.returnValue = 'terminate'));
      }
      return;
    });
  }

  name = 'SCORM';
  doesSupportLegacyData = false;

  init = async (): Promise<boolean> => {
    let connected = this._init();

    let timeout = 1000;
    let retryCount = 0;
    while (!connected) {
      await _delay(timeout);
      timeout *= 2;
      connected = this._init();
      retryCount = retryCount + 1;
      if (retryCount === 4) {
        throw Error('Scorm cannot be initialized.');
      }
    }

    return connected;
  };

  getTrackingData = (): Promise<PersistenceTrackingData> => {
    const suspendData = this._scormClient.get('cmi.suspend_data');
    if (suspendData === 'null' || suspendData === '') {
      const emptyTrackingData: PersistenceTrackingData = {
        widgetTrackingData: createPreviouslySelectedOptionsForItems([]),
        openQuestionAnswers: Map<TrackingIdentifier, Map<TrackingIdentifier, OpenQuestionAnswerEntry>>(),
        finishedItems: Immutable.Set(),
        version: TrackingDataVersion.v2,
      };
      return Promise.resolve(emptyTrackingData);
    }

    const score = parseFloat(this._scormClient.get(this._isScorm12() ? 'cmi.core.score.raw' : 'cmi.score.raw'));
    const successStatus = this._isScorm12()
      ? this._scormClient.get('cmi.core.lesson_status')
      : this._scormClient.get('cmi.success_status');

    const quizStatus: QuizStatus = {
      score: Number.isNaN(score) ? null : score,
      passed: successStatus === 'passed' ? true : successStatus === 'failed' ? false : null,
    };

    const trackingData = CommaSerializer.deserializeData(this._replaceDollarSignWithQuote(suspendData));
    trackingData.quizStatus = quizStatus;

    if (this._cachedOpenQuestionAnswers !== null) {
      trackingData.openQuestionAnswers = this._cachedOpenQuestionAnswers;
    } else if (this._isScorm12()) {
      trackingData.openQuestionAnswers = this._storage.getForLearner(this.getLearnerId()) ?? Map();
      this._cachedOpenQuestionAnswers = trackingData.openQuestionAnswers;
    } else {
      trackingData.openQuestionAnswers =
        getInteractionsFromLms2004(this._scormClient) ?? this._storage.getForLearner(this.getLearnerId()) ?? Map();
      this._cachedOpenQuestionAnswers = trackingData.openQuestionAnswers;
    }

    return Promise.resolve(trackingData);
  };

  async afterSetup(): Promise<void> {
    if (!this._isCourseCompleted()) {
      this._scormClient.set('cmi.exit', 'suspend');
      setTimeout(() => {
        this._scormClient.save();
      }, 500);
    }
  }

  setTrackingData = async ({
    widgetTrackingData,
    finishedItems,
    openQuestionAnswers,
  }: PersistenceTrackingData): Promise<void> => {
    const { openQuestionAnswers: oldInteractions } = await this.getTrackingData();

    const emptyOqAnswers = Map() as OpenQuestionAnswersForItems;
    this._scormClient.set(
      'cmi.suspend_data',
      this._replaceQuoteWithDollarSign(CommaSerializer.serializeData(finishedItems, widgetTrackingData, emptyOqAnswers))
    );

    const updatedInteractions = diffOpenQuestionAnswers(openQuestionAnswers, oldInteractions);
    if (this._isScorm12()) {
      storeInteractionsToLms12(updatedInteractions, this._scormClient);
    } else {
      storeInteractionsToLms2004(updatedInteractions, this._scormClient);
    }

    // Always store to the local storage to ensure data is not lost if LMS doesn't support interactions fully
    this._storage.setForLearner(this.getLearnerId(), openQuestionAnswers);
    // Update in-memory cache to reflect what was just stored
    this._cachedOpenQuestionAnswers = openQuestionAnswers;

    setTimeout(() => {
      this._scormClient.save();
    }, 500);
    return Promise.resolve();
  };

  setQuestinInteraction = (): Promise<void> => {
    return Promise.resolve();
  };

  setQuizStatus = (quizStatus: QuizStatus): Promise<void> => {
    if (quizStatus.score !== null) {
      const scoreInHundredths = Math.round(quizStatus.score * 100);
      const raw = `${scoreInHundredths / 100}`;
      if (this._isScorm12()) {
        this._scormClient.set('cmi.core.score.raw', raw);
        this._scormClient.set('cmi.core.score.max', '100');
        this._scormClient.set('cmi.core.score.min', '0');
        if (!this._isCourseCompleted()) {
          this._scormClient.status(
            'set',
            quizStatus.passed ? CourseCompletionStatus.Passed : CourseCompletionStatus.Failed
          );
        }
      } else {
        this._scormClient.set('cmi.score.raw', raw);
        this._scormClient.set('cmi.score.scaled', `${scoreInHundredths / 10000}`);
        this._scormClient.set('cmi.score.max', '100');
        this._scormClient.set('cmi.score.min', '0');
        if (quizStatus.passed) {
          this._scormClient.set('cmi.success_status', 'passed');
        } else {
          this._scormClient.set('cmi.success_status', 'failed');
        }
      }
      setTimeout(() => {
        this._scormClient.save();
      }, 500);
    }
    return Promise.resolve();
  };

  setCompletionStatus = (newStatus: CourseCompletionStatus): Promise<void> => {
    const currentStatus: CourseCompletionStatus = this._scormClient.status('get');
    if (!(currentStatus === CourseCompletionStatus.Completed || currentStatus === newStatus)) {
      this._scormClient.status('set', newStatus);
      setTimeout(() => {
        this._scormClient.save();
      }, 500);
    }
    return Promise.resolve();
  };

  terminateConnection = (): Promise<void> => {
    this._scormClient.set('cmi.exit', this._isCourseCompleted() ? this._completedExit() : 'suspend');
    this._scormClient.quit();
    return Promise.resolve();
  };

  getLearnerId = (): string => {
    try {
      const learnerId = this._scormClient.get(this._isScorm12() ? 'cmi.core.student_id' : 'cmi.learner_id');
      if (learnerId && learnerId !== '' && learnerId !== 'null') {
        return learnerId;
      }
    } catch {
      // Fall through to default
    }
    return 'anonymous';
  };

  private _init = (): boolean => {
    this._scormClient = scormClient;
    this._scormClient.handleExitMode = false;
    this._scormClient.init();
    return this._scormClient.connection.isActive;
  };

  private _isScorm12 = (): boolean => this._scormClient.version !== '2004';

  private _isCourseCompleted = (): boolean => this._scormClient.status('get') === CourseCompletionStatus.Completed;

  private _completedExit = (): string => (this._isScorm12() ? 'logout' : 'normal');
}
